/**
 * relay-S3-uxfix Fix 4 — Hybrid decisions merge.
 *
 * The Decisions panel originally rendered only `relay_decisions/{ws}/items/`
 * (~8 items today, all from the `create_decision` MCP tool). Travis observed
 * that the team log carries 1370+ posts tagged `decision` that never appear
 * in the panel. This helper merges both sources into a single `Decision[]`
 * the panel can render unchanged.
 *
 * `source: "log"` items keep the original post around via `tags` so the
 * `ProjectBadge` (Fix 3) can derive Relay/Juju/sprint:N/domain attribution
 * from the post's tags. ID spaces don't overlap (`post-<id>` prefix), so a
 * simple concat + sort is enough — no content-similarity dedup needed.
 */

import type { Decision, TeamLogMessage } from "../types";

const HEADING_PREFIX = /^#{1,6}\s+/;
const PROJECT_LIKE = /^(relay|juju|gymflow|crm|documents|auth)$/i;

/** Strip a leading markdown heading marker from a content line. */
function stripHeading(line: string): string {
  return line.replace(HEADING_PREFIX, "").trim();
}

/**
 * Pick the first non-empty line of a post as the decision question.
 * Falls back to a placeholder so the card still renders.
 */
function firstLine(content: string): string {
  for (const raw of content.split(/\r?\n/)) {
    const line = stripHeading(raw);
    if (line) return line;
  }
  return "(empty decision)";
}

/** Body after the first non-empty line, joined back together. */
function remainder(content: string): string | null {
  const lines = content.split(/\r?\n/);
  let i = 0;
  while (i < lines.length && stripHeading(lines[i]) === "") i++;
  // Skip the question line itself.
  i++;
  // Skip leading blank lines after the question.
  while (i < lines.length && lines[i].trim() === "") i++;
  if (i >= lines.length) return null;
  const rest = lines.slice(i).join("\n").trim();
  return rest.length > 0 ? rest : null;
}

/**
 * Best-effort category inference for a log-tagged decision. Used by the
 * existing project-filter dropdown in ERDecisionsPanel (filters by
 * `category`). Falls back to `"log-post"` when no project tag is present.
 */
function inferCategory(tags: string[] | undefined): string {
  if (!tags) return "log-post";
  for (const t of tags) {
    if (PROJECT_LIKE.test(t)) return t.toLowerCase();
  }
  return "log-post";
}

/**
 * Convert one team_log post into a synthetic Decision. Negative `sequence`
 * keeps the namespace disjoint from `#DEC-NNN` server counters; the panel's
 * formatter renders these as `LOG-NNN`.
 */
export function postToDecision(post: TeamLogMessage): Decision {
  const seqValue = typeof post.sequence === "number" ? post.sequence : 0;
  return {
    id: `post-${post.id}`,
    sequence: -Math.abs(seqValue || 0),
    question: firstLine(post.content),
    answer: remainder(post.content),
    rationale: null,
    options_considered: [],
    status: "decided",
    category: inferCategory(post.tags),
    linked_issue: post.linkedIssue ?? null,
    decided_by: post.author ? [post.author] : [],
    createdAt: Date.parse(post.timestamp) || 0,
    decidedAt: Date.parse(post.timestamp) || 0,
    source: "log",
    tags: post.tags,
  };
}

/**
 * Merge structured decisions with log-derived synthetic decisions.
 * Sort: createdAt desc, structured-source first within ties.
 */
export function mergeDecisions(
  structured: Decision[],
  messages: TeamLogMessage[],
): Decision[] {
  const stamped: Decision[] = structured.map((d) =>
    d.source ? d : { ...d, source: "structured" as const },
  );
  const synthetic: Decision[] = [];
  for (const m of messages) {
    if (!m.tags || !m.tags.includes("decision")) continue;
    if (!m.id || !m.timestamp || !m.content) continue;
    synthetic.push(postToDecision(m));
  }
  return [...stamped, ...synthetic].sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    if (a.source === "structured" && b.source !== "structured") return -1;
    if (b.source === "structured" && a.source !== "structured") return 1;
    return 0;
  });
}

/** One `decision`-tagged post as returned by `/api/decisions/history`. */
export interface HistoryDecisionRow {
  sequence?: number;
  author?: string;
  content: string;
  timestamp: string;
  tags?: string[];
}

/**
 * Union of structured decisions, live decision-tagged posts and BigQuery
 * history rows. Live posts are always included, so a decision posted while
 * the view is open shows up even after history has loaded. Log-derived
 * entries are de-duplicated by post sequence (history first); entries
 * without a sequence are kept as-is.
 */
export function mergeDecisionsWithHistory(
  structured: Decision[],
  messages: TeamLogMessage[],
  historyRows: HistoryDecisionRow[],
): Decision[] {
  const synth = historyRows.map((row) =>
    postToDecision({
      id: String(row.sequence || `bq-${row.timestamp}`),
      sequence: row.sequence,
      author: row.author,
      content: row.content,
      timestamp: row.timestamp,
      tags: row.tags,
    } as TeamLogMessage),
  );
  const seen = new Set(synth.map((d) => d.sequence).filter((s) => s !== 0));
  const live = mergeDecisions(structured, messages).filter((d) => {
    if (d.source !== "log" || d.sequence === 0) return true;
    return !seen.has(d.sequence);
  });
  return [...live, ...synth].sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    if (a.source === "structured" && b.source !== "structured") return -1;
    if (b.source === "structured" && a.source !== "structured") return 1;
    return 0;
  });
}
